import * as React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps, Route } from 'react-router-dom';

import AuthCallback from './components/AuthCallback';
import { RootState } from './redux/modules';
import auth0Client from './Auth';
import PrivateRouteCopy from './components/PrivateRouteCopy';

const DashboardPage = React.lazy(() => import('./containers/DashboardPage'));
const SingleCalculationPageCCTArbCR = React.lazy(() => import('./containers/SingleCalculationPageCCTArbCR'));
const BatchCalculationPageCCTArbCR = React.lazy(() => import('./containers/BatchCalculationPageCCTArbCR'));
const SingleCalculationPageTTT = React.lazy(() => import('./containers/SingleCalculationPageTTT'));
const SingleCalculationPageArbitraryCooling = React.lazy(() =>
  import('./containers/SingleCalculationPageArbitraryCooling')
);

const AppWrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  flex-direction: column;
`;

interface RouteType {
  component: React.ComponentType<any>;
  exact: boolean;
  path: string;
}

const routes: RouteType[] = [
  {
    component: DashboardPage,
    exact: true,
    path: '/dashboard'
  },
  {
    component: SingleCalculationPageTTT,
    exact: true,
    path: '/ttt'
  },
  {
    component: SingleCalculationPageArbitraryCooling,
    exact: true,
    path: '/arbitrary-cooling'
  },
  {
    component: SingleCalculationPageCCTArbCR,
    exact: true,
    path: '/cct'
  },
  {
    component: BatchCalculationPageCCTArbCR,
    exact: true,
    path: '/batchcct'
  },
  {
    component: DashboardPage,
    exact: true,
    path: '/'
  }
];

interface Props extends RouteComponentProps<any> { }

interface State {
  checkingSession: boolean;
}

class App extends React.Component<Props, State> {
  private logoutTimer: any;
  constructor(props: Props) {
    super(props);
    this.state = {
      checkingSession: true
    };
  }

  async componentDidMount() {
    if (this.props.location.pathname === '/auth-callback') {
      this.setState({ checkingSession: false });
      return;
    }
    try {
      await auth0Client.silentAuth();
    } catch (err) {
      // If no SSO session available, do not clear local tokens; just show LoginPage via route guard
      const code = err && (err.error || err.code);
      if (code === 'login_required' || code === 'consent_required' || code === 'interaction_required') {
        // Only show expiry banner if we previously had a session
        try {
          const hadSession = !!auth0Client.getExpiresAt();
          if (hadSession) localStorage.setItem('auth.sessionExpired', '1');
        } catch { }
        this.setState({ checkingSession: false });
        return;
      }
    }

    this.setState({ checkingSession: false });
  }

  componentWillUnmount(): void {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  // Auto-logout timer disabled to avoid unexpected redirects; rely on route guards
  public render() {
    const { checkingSession } = this.state;
    const user = auth0Client.getProfile();

    return (
      <AppWrapper>
        <Route
          path="/auth-callback"
          render={props => {
            return <AuthCallback />;
          }}
        />
        <React.Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>}>
          {routes.map((route: RouteType) => (
            <PrivateRouteCopy
              user={user}
              key={route.path}
              {...route}
              checkingSession={checkingSession}
            />
          ))}
        </React.Suspense>
      </AppWrapper>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  return {};
};

const mapDispatchToProps = (dispatch: any) => ({});

const AppContainer = connect(mapStateToProps, mapDispatchToProps)(App); // AppContainer is wrapping App

const AppContainerWithRouter = withRouter(AppContainer);

export default AppContainerWithRouter;
